import { memo, useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { Chip } from '@air/primitive-chip';

import { SelectChipOptionContainer } from '~/components/Select/components/SelectChipOptionContainer';
import { SelectChipRemoveButton } from '~/components/Select/components/SelectChipRemoveButton';
import { Select, type SelectProps } from '~/components/Select/Select';
import { useImportedKeywordsList } from '~/swr-hooks/useImportedKeywordsList';

export type ImportedKeywordsSelectProps = {
  selectedKeywords: string[];
  onSelectedKeywordsChange: (newKeywords: string[]) => void;
  boardId: string | undefined;
  libraryId: string | undefined;
} & Omit<SelectProps, 'options' | 'selectedOptions' | 'onSelectionChange' | 'placeholder'>;

export const ImportedKeywordsSelect = memo(
  ({ onSelectedKeywordsChange, selectedKeywords, boardId, libraryId, ...props }: ImportedKeywordsSelectProps) => {
    const [searchValue, setSearchValue] = useState('');

    const [debouncedSearchValue] = useDebounce(searchValue, 200, {
      trailing: true,
    });

    const { data: keywords = [], isLoading } = useImportedKeywordsList({
      search: debouncedSearchValue,
      boardId,
      libraryId,
    });

    const onSelectionChange = useCallback<SelectProps['onSelectionChange']>(
      ({ chips }) => {
        const keywords = chips.map(({ value }) => value);
        onSelectedKeywordsChange(keywords);
      },
      [onSelectedKeywordsChange],
    );

    const listItemRenderer = useCallback<Required<SelectProps>['listItemRenderer']>(
      (keyword) => (
        <SelectChipOptionContainer className="-mx-0.5 py-2">
          <Chip shape="rounded" size="medium" className="bg-grey-5 px-2 text-grey-11" key={keyword.value}>
            {keyword.label}
          </Chip>
        </SelectChipOptionContainer>
      ),
      [],
    );

    const selectedItemRenderer = useCallback<Required<SelectProps>['chipRenderer']>(
      (keyword) => (
        <Chip shape="rounded" size="medium" className="mr-1.5 bg-grey-5 px-2 text-grey-11" key={keyword.value}>
          {keyword.label}
          <SelectChipRemoveButton
            chip={keyword}
            onClick={() => {
              onSelectedKeywordsChange(selectedKeywords.filter((k) => k !== keyword.value));
            }}
          />
        </Chip>
      ),
      [onSelectedKeywordsChange, selectedKeywords],
    );

    const options = useMemo(() => keywords.map((value) => ({ value, label: value })), [keywords]);

    return (
      <Select
        options={options}
        maxDropdownHeight={280}
        isLoading={isLoading}
        selectedOptions={selectedKeywords.map((keyword) => ({ value: keyword, label: keyword }))}
        onSelectionChange={onSelectionChange}
        onInputChange={setSearchValue}
        renderAsInput={true}
        isClearable={true}
        isSearchable={true}
        listItemRenderer={listItemRenderer}
        chipRenderer={selectedItemRenderer}
        hasDropdownOverlay
        isSticky
        placeholder="Search for keywords"
        size="large"
        {...props}
      />
    );
  },
);

ImportedKeywordsSelect.displayName = 'ImportedKeywordsSelect';
